import { RefObject, useEffect, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { isEmpty, isNil, noop } from 'lodash';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FieldArray, FormikProvider, useFormik } from 'formik';
// Import Types
import type { RowItem } from './types';
// Import Helpers
import { genericMemo } from 'services/Helpers';
import { mapToFormModel } from './helpers';
// Import Components
import DraggableRow from './components/DraggableRow';
import PreviewRow from './components/PreviewRow';
// Import Styles
import styles from './draggableList.module.scss';

/**
 * DraggableForm
 *
 * Renders a form with draggable rows using Formik and react-dnd. Supports validation, error handling, and custom row rendering.
 *
 * @template R - Row data type (should extend RowItem)
 *
 * @example
 * import { useRef, useCallback } from 'react';
 * import * as Yup from 'yup';
 * import { DraggableForm, draggableFormKey } from 'components/DraggableForm';
 * import CustomRow, { exampleData, customRowStyles, RowsDropdownData } from 'components/DraggableForm/components/CustomRow';
 *
 * const validationSchema = Yup.object().shape({
 *   [draggableFormKey]: Yup.array().of(
 *     Yup.object().shape({
 *       isTurnedOn: Yup.boolean().required('Must Be Boolean'),
 *       dropdown1: Yup.string().nullable(true).required('Required Field'),
 *       dropdown2: Yup.string().nullable(true).required('Required Field'),
 *     })
 *   ),
 * });
 *
 * const errors = [undefined, { isTurnedOn: 'Must Be Boolean', dropdown1: 'Error text' }, { dropdown2: 'Example' }];
 *
 * @example 1. When the form using errors from outside
 * const Example1 = () => {
 *   const formRef = useRef<{ validateForm?: () => void }>(null);
 *   const handleChange = useCallback((values) => console.log(values), []);
 *   const handleErrors = useCallback((errors) => console.log(errors), []);
 *   return (
 *     <DraggableForm
 *       data={exampleData}
 *       idKey="id"
 *       formRef={formRef}
 *       formContainerClassName={customRowStyles.formContainerClassName}
 *       rowContainerClassName={customRowStyles.rowContainerClassName}
 *       rowContentClassName={customRowStyles.rowContentClassName}
 *       onChange={handleChange}
 *       errors={errors}
 *       rowRenderer={(props, index, parentKey, isDragging) => (
 *         <CustomRow
 *           {...props}
 *           index={index}
 *           parentName={parentKey}
 *           isDragging={isDragging}
 *           dropDownData={RowsDropdownData[props.id]}
 *           getDynamicVariables={() => {}}
 *         />
 *       )}
 *     />
 *   );
 * };
 *
 * @example 2. When the form using validation schema to handle errors itself
 * const Example2 = () => {
 *   const formRef = useRef<{ validateForm?: () => void }>(null);
 *   const handleChange = useCallback((values) => console.log(values), []);
 *   const handleErrors = useCallback((errors) => console.log(errors), []);
 *   return (
 *     <DraggableForm
 *       data={exampleData}
 *       idKey="id"
 *       formRef={formRef}
 *       formContainerClassName={customRowStyles.formContainerClassName}
 *       rowContainerClassName={customRowStyles.rowContainerClassName}
 *       rowContentClassName={customRowStyles.rowContentClassName}
 *       onChange={handleChange}
 *       validationSchema={validationSchema}
 *       getValidationSchemaErrors={handleErrors}
 *       rowRenderer={(props, index, parentKey, isDragging) => (
 *         <CustomRow
 *           {...props}
 *           index={index}
 *           parentName={parentKey}
 *           isDragging={isDragging}
 *           dropDownData={RowsDropdownData[props.id]}
 *           getDynamicVariables={() => {}}
 *         />
 *       )}
 *     />
 *   );
 * };
 */

/**
 * Key used for the draggable form field in Formik
 */
export const draggableFormKey = 'draggableList';

/**
 * Props for DraggableForm component
 * @template R - Generic Row data type (should be a specific custom row data type)
 * @property {R[]} data - Array of row data items
 * @property {string} [idKey] - Key used to identify each row (default: 'id')
 * @property {RefObject<{ validateForm?: () => void }>} [formRef] - Ref for manually triggering form validation
 * @property {string} [formContainerClassName] - Class name for form container
 * @property {string} [rowContainerClassName] - Class name for row container
 * @property {string} [rowContentClassName] - Class name for row content
 * @property {Nilable<AnyObject | string>[]} [errors] - Array of errors for each row
 * @property {AnyObject} [validationSchema] - Validation schema for Formik
 * @property {(errors?: Nilable<AnyObject | string>[] | string) => void} [getValidationSchemaErrors] - Callback to get validation errors from schema
 * @property {(values: R[]) => void} onChange - Callback when form values change
 * @property {(props: R, index: number, parentName: string, isDragging: boolean) => React.ReactNode} rowRenderer - Function to render each row
 */
interface Props<R extends RowItem> {
    data: R[];
    idKey?: string;
    formRef?: RefObject<{ validateForm?: () => void }>;
    formContainerClassName?: string;
    rowContainerClassName?: string;
    rowContentClassName?: string;
    errors?: Nilable<AnyObject | string>[];
    validationSchema?: AnyObject;
    getValidationSchemaErrors?: (errors?: Nilable<AnyObject | string>[] | string) => void;
    onChange: (values: R[]) => void;
    rowRenderer: (props: R, index: number, parentName: string, isDragging: boolean) => React.ReactNode;
}

/**
 * DraggableForm component renders a form with draggable rows using Formik and react-dnd.
 * Supports validation, error handling, and custom row rendering.
 *
 * @template R - Generic Row data type (should be a specific custom row data type)
 * @param {Props<R>} props - DraggableForm props
 * @returns {JSX.Element} DraggableForm component
 */
const DraggableForm = <R extends RowItem>(props: Props<R>) => {
    const {
        data,
        idKey = 'id',
        formRef,
        formContainerClassName,
        rowContainerClassName,
        rowContentClassName,
        errors,
        validationSchema,
        getValidationSchemaErrors,
        onChange,
        rowRenderer,
    } = props;

    const formik = useFormik<{ [draggableFormKey]: (R & { index: number })[] }>({
        initialValues: { [draggableFormKey]: mapToFormModel(data, idKey) },
        onSubmit: noop,
        validate: isNil(validationSchema) && !isEmpty(errors) ? () => ({ [draggableFormKey]: errors }) : undefined,
        validationSchema,
        validateOnBlur: true,
        validateOnMount: true,
        validateOnChange: true,
    });

    const { values, errors: formErrors } = formik;

    /**
     * Call onChange whenever form values change
     */
    const valuesJSONString = JSON.stringify(values[draggableFormKey]);
    useEffect(() => {
        onChange(values[draggableFormKey]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onChange, valuesJSONString]);

    /**
     * Call getValidationSchemaErrors when validation schema or errors change
     */
    useEffect(() => {
        if (!isNil(validationSchema)) {
            getValidationSchemaErrors?.(formErrors?.[draggableFormKey]);
        }
    }, [formErrors, getValidationSchemaErrors, validationSchema]);

    /**
     * Expose validateForm method via ref
     */
    useImperativeHandle(
        formRef,
        () => ({
            validateForm: formik.submitForm,
        }),
        [formik],
    );

    return (
        <div className={classNames(styles.container, formContainerClassName)}>
            <FormikProvider value={formik}>
                <DndProvider backend={HTML5Backend} context={window}>
                    <FieldArray
                        name={draggableFormKey}
                        render={({ form: { values }, move }) => {
                            const items = values[draggableFormKey] as R[];
                            return (
                                <>
                                    {items.map((item, index) => (
                                        <DraggableRow<R>
                                            key={item[idKey]}
                                            isDraggable={!!item.isDraggable}
                                            rowContainerClassName={rowContainerClassName}
                                            rowContentClassName={rowContentClassName}
                                            getDraggingRow={() => item}
                                            handleRowDrop={(draggedItem: R, targetItem: R) =>
                                                move(
                                                    items.findIndex((i) => i[idKey] === draggedItem[idKey]),
                                                    items.findIndex((i) => i[idKey] === targetItem[idKey]),
                                                )
                                            }
                                            content={rowRenderer?.(item, index, draggableFormKey, false) || item.name}
                                        />
                                    ))}

                                    <PreviewRow
                                        formName={draggableFormKey}
                                        rowContainerClassName={rowContainerClassName}
                                        rowContentClassName={rowContentClassName}
                                        rowRenderer={rowRenderer}
                                    />
                                </>
                            );
                        }}
                    />
                </DndProvider>
            </FormikProvider>
        </div>
    );
};

export default genericMemo(DraggableForm);
